import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import FilmCard from "@/components/feature/FilmCard";
import SpecialCard from "@/components/feature/SpecialCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getFilmById, getSpecialById } from "@/utils/film";

export default function SpecialPage() {
  const { id } = useParams();
  const special = useMemo(() => getSpecialById(id), [id]);

  useDocumentMeta(
    special ? `妖精网站 - ${special.title}专题合集` : "妖精网站 - 未找到该专题",
    special ? `${special.title}：${special.intro}` : undefined
  );

  if (!special) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-bookmark-3-line text-3xl" />
        </span>
        <p className="font-heading text-lg font-semibold text-foreground-800">未找到该专题</p>
        <p className="mt-2 text-sm text-foreground-500">该专题可能已被移除或链接有误</p>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const specialFilms = special.filmIds
    .map((filmId) => getFilmById(filmId))
    .filter((film): film is NonNullable<ReturnType<typeof getFilmById>> => Boolean(film));

  const relatedSpecials = special.relatedSpecialIds
    .map((item) => getSpecialById(item))
    .filter((item): item is NonNullable<ReturnType<typeof getSpecialById>> => Boolean(item));

  return (
    <div className="w-full">
      {/* 专题头部 */}
      <section className="relative h-[300px] w-full overflow-hidden md:h-[400px]">
        <img
          src={special.cover}
          alt={`${special.title}专题主图`}
          title={`${special.title} 妖精影视专题`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1200px] px-4 pb-8 md:px-8 md:pb-10">
            <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm text-background-300">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span>专题合集</span>
            </nav>
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
              <i className="ri-bookmark-3-fill" />
              妖精专题
            </span>
            <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">
              {special.title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              {special.intro}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回首页
        </Link>

        <section>
          <SectionHeading
            title="专题收录作品"
            subtitle={`本专题共收录 ${specialFilms.length} 部妖精影视作品`}
            icon="ri-film-line"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {specialFilms.map((film) => (
              <FilmCard key={film.id} film={film} showIntro={false} />
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-quill-pen-line text-accent-600" />
            推荐理由
          </h2>
          <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
            {special.reason}
          </p>
        </section>

        {relatedSpecials.length > 0 && (
          <section className="mt-12">
            <SectionHeading
              title="相关专题"
              subtitle="继续探索其他妖精主题合集"
              icon="ri-links-line"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
              {relatedSpecials.map((item) => (
                <SpecialCard key={item.id} special={item} filmCount={item.filmIds.length} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}